<template>
    <div>
        <h3 class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText">
            {{ $t('Sachkonten') }}
        </h3>
        <div
            class="kgr-alert kgr-alert--info kgr-row kgr-text kgr-text--preserveLineBreaks"
        >
            {{ $t('club.accounting.impersonalAccount.infoText') }}
        </div>
        <div
            v-if="isCreatable"
            class="kgr-row kgr-text--rightAligned"
        >
            <button
                class="kgr-button kgr-button--accent"
                @click="openCreateDialog"
            >
                {{ $t('Sachkonto hinzufügen') }}
            </button>
        </div>
        <table class="kgr-table kgr-table--block kgr-table--striped printable">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Nummer') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Bezeichnung') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions kgr-hidden-print"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loading">
                    <td
                        colspan="3"
                        style="text-align: center;"
                    >
                        <kgr-loader />
                    </td>
                </tr>
                <tr v-else-if="impersonalAccounts.length === 0">
                    <td
                        colspan="3"
                        style="text-align: center;"
                    >
                        {{ $t('dataTable.noData') }}
                    </td>
                </tr>
                <template v-else>
                    <tr
                        v-for="impersonalAccount in impersonalAccounts"
                        :key="impersonalAccount.id"
                        class="kgr-table__row"
                    >
                        <td class="kgr-table__cell">
                            {{ impersonalAccount.number }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ impersonalAccount.label }}
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--actions kgr-hidden-print">
                            <button
                                :disabled="!impersonalAccount.isEditable"
                                class="kgr-table__action"
                                @click="openEditDialog(impersonalAccount.id)"
                            >
                                <kgr-icon
                                    icon-class="edit"
                                    :class="{ 'kgr-icon--light noPointer': !impersonalAccount.isEditable }"
                                />
                            </button>
                            <button
                                :disabled="!impersonalAccount.isDeletable"
                                class="kgr-table__action"
                                @click="deleteImpersonalAccount(impersonalAccount.id)"
                            >
                                <kgr-icon
                                    icon-class="delete"
                                    :class="{ 'kgr-icon--light noPointer': !impersonalAccount.isDeletable }"
                                />
                            </button>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
        <kgr-lightbox
            v-if="modalAccount"
            :headline="modalAccount.id ? modalAccount.number : $t('Sachkonto hinzufügen')"
            :value="showDialog"
            animated
            @post-input="closeModal"
        >
            <div
                class="kgr-alert kgr-alert--info kgr-row"
            >
                {{ $t('Bitte beachte, dass nur Sachkonto-Nummern in DATEV eingelesen werden können.') }}
            </div>
            <kgr-form-row
                :label="$t('Nummer')"
                :error="error"
                required
                field-name="number"
            >
                <kgr-text-input v-model.trim="modalAccount.number" />
            </kgr-form-row>
            <kgr-form-row
                :label="$t('Bezeichnung')"
                :error="error"
                field-name="label"
            >
                <kgr-text-input v-model.trim="modalAccount.label" />
            </kgr-form-row>
            <template #footer>
                <button
                    class="kgr-button"
                    @click="closeModal"
                >
                    {{ $t('cancel') }}
                </button>
                <button
                    class="kgr-button kgr-button--accent"
                    :disabled="!modalAccount.number || modalAccount.number === ''"
                    @click="modalAccount.id ? updateImpersonalAccount() : createImpersonalAccount()"
                >
                    {{ $t('save') }}
                </button>
            </template>
        </kgr-lightbox>
    </div>
</template>

<script>
import ClubImpersonalAccount from '../../../js/dto/ClubImpersonalAccount.js';
import ClubImpersonalAccountRepository from '../../../js/repository/club/accounting/ClubImpersonalAccountRepository.js';
import Error from '../../../js/model/Error.js';
import FormRow from '../../../components/common/form/FormRow.vue';
import Icon from '../../../components/common/icon/Icon.vue';
import Lightbox from '../../../components/common/modal/Lightbox.vue';
import Loading from '../../../components/common/Loading.vue';
import TextInput from '../../../components/common/form/input/TextInput.vue';

export default {
    components: {
        'kgr-form-row': FormRow,
        'kgr-icon': Icon,
        'kgr-lightbox': Lightbox,
        'kgr-loader': Loading,
        'kgr-text-input': TextInput,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        isCreatable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            error: new Error(),
            impersonalAccounts: [],
            modalAccount: null,
        }
    },
    created() {
        this.loadImpersonalAccounts();
    },
    methods: {
        loadImpersonalAccounts() {
            this.loading = true;
            ClubImpersonalAccountRepository
                .findAll(this.clubId)
                .then(data => {
                    this.impersonalAccounts = data;
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(this.loading = false);
        },
        updateImpersonalAccount() {
            ClubImpersonalAccountRepository
                .update(this.modalAccount)
                .then(() => {
                    this.loadImpersonalAccounts();
                    this.closeModal();
                })
                .catch(() => this.$notify.error('saveChanges.error'));
        },
        createImpersonalAccount() {
            this.loading = true;
            ClubImpersonalAccountRepository
                .create(this.clubId, this.modalAccount)
                .then(() => {
                    this.loadImpersonalAccounts();
                    this.closeModal();
                })
                .catch(() => this.$notify.error('saveChanges.error'));
        },
        deleteImpersonalAccount(impersonalAccountId) {
            showConfirm(this.$t('confirmInformal'), this.$t('Willst du dieses Sachkonto wirklich löschen?'), () => {
                ClubImpersonalAccountRepository
                    .delete(impersonalAccountId)
                    .then(() => this.loadImpersonalAccounts())
                    .catch(() => this.$notify.error('saveChanges.error'));
            }, this.$t('Löschen'));
        },
        openEditDialog(id) {
            this.modalAccount = this.impersonalAccounts.find(impersonalAccount => impersonalAccount.id === id);
            this.showDialog = true;
        },
        openCreateDialog() {
            this.modalAccount = new ClubImpersonalAccount();

            this.showDialog = true;
        },
        closeModal() {
            this.modalAccount = null;
            this.showDialog = false;
        },
    },
}
</script>

<style scoped lang="scss">
.noPointer {
    pointer-events: none
}
</style>
